import React, { useMemo, useState } from 'react';
import ProductRow from './ProductRow';
import ProductDetailPanel from './ProductDetailPanel';
import FilterBar from './FilterBar';
import HeaderRow from './HeaderRow';
import Pagination from './Pagination';
import CardLoader from '../../common/CardLoader';
import DeleteConfirmModal from './DeleteConfirmModal';
import './ProductTable.css';

const ProductTable = ({
  products = [],
  total = 0,
  onRowChange,
  onFilterChange,
  onPageChange,
  onSort,
  filters = {},
  currentPage = 1,
  pageSize = 10,
  sortField = 'flavor',
  sortDirection = 'asc',
  isLoading = false,
  error = null,
  onDeleteProduct,
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [initialTab, setInitialTab] = useState('overview');
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);

  const totalPages = Math.ceil(total / pageSize);

  const sortedProducts = useMemo(() => {
    if (!sortField) return products;
    return [...products].sort((a, b) => {
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDirection === 'asc' ? av - bv : bv - av;
      }
      const cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [products, sortField, sortDirection]);

  const handleSelectProduct = (product, tab = 'overview') => {
    setSelectedProduct(product);
    setInitialTab(tab);
  };

  const handleSave = async (productData) => {
    if (!selectedProduct) return;
    setIsSaving(true);
    try {
      const product = products.find((p) => p.id === selectedProduct.id);
      const meta = product
        ? { version: product.version, updatedAt: product.updated_at || product.created_at }
        : {};
      await onRowChange(selectedProduct.id, productData, meta);
      setSelectedProduct(null);
      setValidationErrors({});
    } catch (err) {
      if (err.response?.data?.errors) setValidationErrors(err.response.data.errors);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteTarget(products.find((p) => p.id === id) ?? null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await onDeleteProduct(deleteTarget.id);
    setDeleteTarget(null);
  };

  if (isLoading) {
    return (
      <div className="product-container loading">
        <CardLoader message="Loading products..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-container error">
        <p>Error loading products: {error.message}</p>
      </div>
    );
  }

  return (
    <>
      <div className="product-container">
        <FilterBar filters={filters} onFilterChange={onFilterChange} />

        {products.length === 0 ? (
          <div className="product-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h3>No products found</h3>
            <p>Try adjusting your search or filters.</p>
          </div>
        ) : (
          <>
            <HeaderRow
              onSort={onSort}
              sortField={sortField}
              sortDirection={sortDirection}
            />
            <div className="product-list">
              {sortedProducts.map((product) => (
                <ProductRow
                  key={product.id}
                  product={product}
                  onSelect={handleSelectProduct}
                  onDelete={handleDeleteClick}
                />
              ))}
            </div>
            <div className="product-footer">
              <span className="showing-info">Showing {products.length} of {total}</span>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
              />
            </div>
          </>
        )}
      </div>

      {selectedProduct && (
        <ProductDetailPanel
          product={selectedProduct}
          initialTab={initialTab}
          onClose={() => setSelectedProduct(null)}
          onSave={handleSave}
          isSaving={isSaving}
          validationErrors={validationErrors}
        />
      )}

      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        productName={deleteTarget?.flavor ?? 'this product'}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default ProductTable;
